import React, { useEffect, useState } from "react";
import { api, formatApiError, API } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Send, Trash2, FileDown, Copy, MessageCircle, CheckCircle2, XCircle, Clock, Users } from "lucide-react";
import { formatDate } from "../lib/constants";
import CooperadosManager from "./CooperadosManager";

export default function DispatchesManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [presetCoop, setPresetCoop] = useState(null);
  // Modal seletor
  // mode: null | "single" | "bulk"
  const [selectorMode, setSelectorMode] = useState(null);
  // Bulk dialog
  const [bulkRecipients, setBulkRecipients] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = () => api.get("/secretariat/dispatches").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const onCoopSendClick = (coop) => {
    setPresetCoop(coop);
    setSelectorMode(null);
    setOpen(true);
  };

  const onBulkSendClick = (coops) => {
    setBulkRecipients(coops);
    setSelectorMode(null);
    setBulkOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Remover este encaminhamento? Esta ação não pode ser desfeita.")) return;
    try { await api.delete(`/secretariat/dispatches/${id}`); toast.success("Removido."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = (id, name) => {
    const token = localStorage.getItem("coomflona_token");
    fetch(`${API}/secretariat/dispatches/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("Erro"); return r.blob(); })
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `protocolo_${name}.pdf`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      })
      .catch(() => toast.error("Não foi possível gerar o PDF."));
  };

  const buildDispatchLink = (token) => `${window.location.origin}/encaminhamento/${token}`;

  const sendWhatsApp = (d) => {
    const link = buildDispatchLink(d.token);
    const protoLine = d.protocol ? `\n(Protocolo nº ${d.protocol})` : "";
    const msg = `Olá, ${d.cooperado_name}! A Secretaria da COOMFLONA está te encaminhando: *${d.title}*${protoLine}\n\nClique no link para visualizar e responder (aceito / não aceito):\n${link}`;
    window.open(`https://wa.me/${d.cooperado_phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="mt-8" data-testid="dispatches-manager">
      <div className="bg-forest-700 text-cream-100 p-5 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Send className="w-5 h-5 text-copaiba-300" />
          <div className="font-display text-xl">Encaminhamentos com protocolo de aceite</div>
        </div>
        <p className="text-cream-100/85 text-sm leading-relaxed">
          Selecione um(a) cooperado(a) ou vários ao mesmo tempo, escreva o conteúdo e envie pelo WhatsApp.
          Cada encaminhamento recebe um <strong>número de protocolo</strong> único (ex.: ENC-2026-0001) e um link
          exclusivo para o destinatário responder com <strong>Aceito</strong> ou <strong>Não Aceito</strong>.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <Button onClick={() => { setPresetCoop(null); setSelectorMode("single"); }} data-testid="select-coop-button" className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
          <Send className="w-4 h-4 mr-2" /> Novo encaminhamento
        </Button>
        <Button onClick={() => setSelectorMode("bulk")} data-testid="select-bulk-button" className="bg-copaiba-500 hover:bg-copaiba-300 text-forest-900 rounded-none">
          <Users className="w-4 h-4 mr-2" /> Enviar para vários cooperados
        </Button>
      </div>

      {selectorMode && (
        <div className="bg-cream-200 border border-cream-400 p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-lg text-forest-900">
              {selectorMode === "bulk" ? "Selecione os destinatários (envio em massa)" : "Escolha o cooperado destinatário"}
            </div>
            <button onClick={() => setSelectorMode(null)} className="text-sm text-forest-700 hover:text-copaiba-700">Cancelar</button>
          </div>
          <CooperadosManager
            onSend={selectorMode === "single" ? onCoopSendClick : undefined}
            selectionMode={selectorMode === "bulk"}
            onBulkSend={selectorMode === "bulk" ? onBulkSendClick : undefined}
          />
        </div>
      )}

      <h2 className="font-display text-2xl text-forest-900 mb-4">Histórico ({items.length})</h2>
      <div className="space-y-3">
        {items.map((d) => (
          <div key={d.id} data-testid={`dispatch-${d.id}`} className="bg-white border border-cream-400 p-5">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusBadge response={d.response} />
                  {d.protocol && (
                    <span data-testid={`dispatch-protocol-${d.id}`} className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 bg-cream-100 border border-cream-400 text-forest-900 font-mono">
                      {d.protocol}
                    </span>
                  )}
                  <span className="text-xs text-forest-900/65">{formatDate(d.created_at)}</span>
                </div>
                <div className="font-display text-xl text-forest-900 mt-2">{d.title}</div>
                <div className="text-sm text-forest-900/75 mt-1">
                  Para: <strong>{d.cooperado_name}</strong> {d.cooperado_is_cooperado ? "(Cooperado)" : "(Não cooperado)"}
                </div>
                <p className="text-sm text-forest-900/80 mt-3 whitespace-pre-wrap line-clamp-3">{d.body}</p>
                {d.response_message && (
                  <div className="mt-3 bg-cream-100 border-l-2 border-copaiba-500 p-3 text-sm text-forest-900/80">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-forest-700 mb-1">Resposta do destinatário</div>
                    {d.response_message}
                  </div>
                )}
              </div>
              <div className="flex md:flex-col gap-2 shrink-0">
                <button
                  onClick={() => sendWhatsApp(d)}
                  data-testid={`disp-whatsapp-${d.id}`}
                  className="p-2 text-[#25D366] hover:bg-cream-200"
                  title="Enviar pelo WhatsApp"
                >
                  <MessageCircle className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(buildDispatchLink(d.token));
                    toast.success("Link copiado!");
                  }}
                  data-testid={`disp-copy-${d.id}`}
                  className="p-2 text-forest-700 hover:bg-cream-200"
                  title="Copiar link"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => downloadPdf(d.id, d.cooperado_name)}
                  data-testid={`disp-pdf-${d.id}`}
                  className="p-2 text-forest-700 hover:bg-cream-200"
                  title="Baixar protocolo PDF"
                >
                  <FileDown className="w-4 h-4" />
                </button>
                <button onClick={() => del(d.id)} data-testid={`disp-delete-${d.id}`} className="p-2 text-terracotta-500 hover:bg-cream-200"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="bg-cream-200 border border-cream-400 p-10 text-center text-forest-900/65">
            Nenhum encaminhamento criado ainda.
          </div>
        )}
      </div>

      <DispatchDialog
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setPresetCoop(null); }}
        cooperado={presetCoop}
        onSent={(d) => {
          setOpen(false);
          load();
          const link = `${window.location.origin}/encaminhamento/${d.token}`;
          const protoLine = d.protocol ? `\n(Protocolo nº ${d.protocol})` : "";
          const msg = `Olá, ${d.cooperado_name}! A Secretaria da COOMFLONA está te encaminhando: *${d.title}*${protoLine}\n\nClique no link para visualizar e responder (aceito / não aceito):\n${link}`;
          window.open(`https://wa.me/${d.cooperado_phone}?text=${encodeURIComponent(msg)}`, "_blank");
        }}
      />

      <BulkDispatchDialog
        open={bulkOpen}
        onOpenChange={(o) => { setBulkOpen(o); if (!o) setBulkRecipients([]); }}
        recipients={bulkRecipients}
        onSent={(createdList) => {
          setBulkOpen(false);
          setBulkRecipients([]);
          load();
          toast.success(`${createdList.length} encaminhamentos criados! Abrindo WhatsApp para cada destinatário...`);
          // open WhatsApp for each (browsers may block too many; we stagger)
          createdList.forEach((d, idx) => {
            setTimeout(() => {
              const link = `${window.location.origin}/encaminhamento/${d.token}`;
              const protoLine = d.protocol ? `\n(Protocolo nº ${d.protocol})` : "";
              const msg = `Olá, ${d.cooperado_name}! A Secretaria da COOMFLONA está te encaminhando: *${d.title}*${protoLine}\n\nClique no link para visualizar e responder (aceito / não aceito):\n${link}`;
              window.open(`https://wa.me/${d.cooperado_phone}?text=${encodeURIComponent(msg)}`, "_blank");
            }, idx * 600);
          });
        }}
      />
    </div>
  );
}

function StatusBadge({ response }) {
  if (response === "aceito") {
    return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-forest-700 text-cream-100"><CheckCircle2 className="w-3 h-3" /> Aceito</span>;
  }
  if (response === "nao_aceito") {
    return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-terracotta-500 text-cream-100"><XCircle className="w-3 h-3" /> Não aceito</span>;
  }
  return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-copaiba-500 text-forest-900"><Clock className="w-3 h-3" /> Aguardando</span>;
}

function DispatchDialog({ open, onOpenChange, cooperado, onSent }) {
  const [form, setForm] = useState({ title: "", body: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) setForm({ title: "", body: "" });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!cooperado) return;
    setSending(true);
    try {
      const { data } = await api.post("/secretariat/dispatches", {
        cooperado_id: cooperado.id,
        title: form.title,
        body: form.body,
      });
      toast.success(`Encaminhamento criado! Protocolo nº ${data.protocol}.`);
      onSent(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Novo encaminhamento</DialogTitle>
          {cooperado && (
            <div className="text-sm text-forest-900/70 mt-1">
              Destinatário: <strong>{cooperado.name}</strong> · {cooperado.phone_display}
              {!cooperado.is_cooperado && <span className="ml-2 text-xs px-2 py-0.5 bg-copaiba-500 text-forest-900">Não cooperado</span>}
            </div>
          )}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Título / Assunto</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="dispatch-form-title" placeholder="Ex.: Convocação para reunião extraordinária" />
          </div>
          <div>
            <Label>Conteúdo do encaminhamento</Label>
            <Textarea rows={8} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required data-testid="dispatch-form-body" placeholder="Escreva aqui o conteúdo da comunicação que será enviada ao cooperado..." />
          </div>
          <div className="bg-cream-200 border border-cream-400 p-4 text-sm text-forest-900/80">
            <strong>Como funciona:</strong> ao clicar em &quot;Gerar e enviar&quot;, o sistema cria um <strong>número de protocolo</strong> e um link exclusivo, salva o
            encaminhamento e abre o WhatsApp com a mensagem pronta para você confirmar o envio. O destinatário responde
            pelo próprio link, sem precisar fazer login.
          </div>
          <DialogFooter>
            <Button type="submit" disabled={sending || !cooperado} data-testid="dispatch-form-submit" className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
              <Send className="w-4 h-4 mr-2" />
              {sending ? "Criando..." : "Gerar e enviar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BulkDispatchDialog({ open, onOpenChange, recipients, onSent }) {
  const [form, setForm] = useState({ title: "", body: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) setForm({ title: "", body: "" });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (recipients.length === 0) return;
    setSending(true);
    try {
      const { data } = await api.post("/secretariat/dispatches/bulk", {
        cooperado_ids: recipients.map((c) => c.id),
        title: form.title,
        body: form.body,
      });
      onSent(data.created || []);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Envio em massa</DialogTitle>
          <div className="text-sm text-forest-900/70 mt-1" data-testid="bulk-recipients-count">
            <strong>{recipients.length}</strong> destinatário(s) selecionado(s)
          </div>
        </DialogHeader>

        {recipients.length > 0 && (
          <div className="bg-cream-200 border border-cream-400 p-3 max-h-40 overflow-y-auto" data-testid="bulk-recipients-list">
            <div className="text-[10px] uppercase tracking-[0.18em] text-forest-700 mb-2">Destinatários</div>
            <div className="flex flex-wrap gap-2">
              {recipients.map((c) => (
                <span key={c.id} className="text-xs px-2 py-1 bg-white border border-cream-400 text-forest-900">
                  {c.name}
                </span>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Título / Assunto</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="bulk-form-title" placeholder="Ex.: Convocação Assembleia Geral 2026" />
          </div>
          <div>
            <Label>Conteúdo do encaminhamento</Label>
            <Textarea rows={8} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required data-testid="bulk-form-body" placeholder="Esta mesma mensagem será enviada a todos os destinatários selecionados. Cada um recebe um link e protocolo exclusivos." />
          </div>
          <div className="bg-cream-200 border border-cream-400 p-4 text-sm text-forest-900/80">
            <strong>Como funciona:</strong> ao clicar em &quot;Gerar e enviar&quot;, o sistema cria <strong>{recipients.length}</strong> encaminhamentos —
            cada cooperado recebe um <strong>número de protocolo</strong> e link próprios. O WhatsApp abrirá para cada destinatário em sequência (com pequeno intervalo).
            <br />
            <span className="text-xs text-forest-900/65">Dica: o navegador pode pedir para autorizar múltiplas janelas — autorize para que todas as conversas abram.</span>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={sending || recipients.length === 0} data-testid="bulk-form-submit" className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
              <Users className="w-4 h-4 mr-2" />
              {sending ? `Criando ${recipients.length} encaminhamentos...` : `Gerar e enviar para ${recipients.length} cooperado(s)`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
